import React from 'react';
import {
  CasNumbersCell,
  CategoriesCell,
  PesticideGroupsCell,
  ChemicalFamiliesCell,
  ResistanceRiskCell,
  RiskSymbolCell,
  ActionLinkCell,
  TagsCell,
} from '@client/components/ui/table/TableCells';
import TruncatedText from '@client/components/ui/TruncatedText';

/**
 * Factory function for expanded row rendering
 *
 * Creates a render function that displays additional record information
 * in an expandable row format using organized tag groups.
 *
 * @returns {Function} Expanded row render function
 *
 * @example
 * const expandedRowRender = createExpandedRowRender();
 * <Table expandable={{ expandedRowRender }} />
 */
// eslint-disable-next-line react/display-name
export const createExpandedRowRender = (expandedCultureIds) => (record) => {
  // eslint-disable-next-line no-undef
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };
  console.log('Length of brute culture', record.length);

  const cultures = record.cultures || [];
  // If expanded culture IDs are available, only show cultures that matched the filter
  const filteredCultures = expandedCultureIds?.length
    ? cultures.filter((c) => expandedCultureIds.includes(c.id))
    : cultures;

  filteredCultures.forEach((culture) => {
    addUnique(culture.id, culture.french_name);
  });

  if (uniqueItems.size === 0) return null;

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

/**
 * Complete table columns configuration for active substances
 *
 * Defines all columns including their rendering, sorting behavior,
 * and display properties. Supports both server-side and client-side sorting.
 *
 * @param {Function} t - Internationalization function
 * @param {Object} currentSorting - Current sorting state
 * @param {string} currentSorting.field - Currently sorted field
 * @param {string} currentSorting.order - Current sort order ('ascend'|'descend')
 * @returns {Array} Complete Antd Table columns configuration
 *
 * @example
 * const columns = createTableColumns(t, { field: 'name', order: 'ascend' });
 * <Table columns={columns} />
 */
export const createTableColumns = (t, currentSorting) => [
  {
    title: t('active-substance|table.Active substance name'),
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (text, record) => (
      <a
        href={`/matieres-actives/details/${record.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-[#105235] hover:text-[#105235] !font-[700] no-underline hover:underline text-[12px]"
      >
        <TruncatedText text={text} maxLength={35} />
      </a>
    ),
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
  },
  {
    title: t('active-substance|table.CAS number'),
    dataIndex: 'cas_numbers',
    key: 'cas_numbers',
    render: (casNumbers) => <CasNumbersCell casNumbers={casNumbers} />,
    sorter: true,
    width: 100,
    sortOrder:
      currentSorting?.field === 'cas_numbers' ? currentSorting.order : null,
  },
  {
    title: t('active-substance|table.Pesticide category'),
    dataIndex: 'pesticide_categories',
    key: 'pesticide_categories',
    render: (categories) => <CategoriesCell categories={categories} />,
    sorter: true,
    sortOrder:
      currentSorting?.field === 'pesticide_categories'
        ? currentSorting.order
        : null,
    width: 100,
  },
  {
    title: t('active-substance|table.Chemical or biological family'),
    key: 'chemical_families',
    dataIndex: 'pesticide_group_associations',
    render: (associations) => (
      <ChemicalFamiliesCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'chemical_families'
        ? currentSorting.order
        : null,
    width: 150,
  },
  {
    title: t('active-substance|table.Pesticide groups'),
    dataIndex: 'pesticide_group_associations',
    key: 'pesticide_groups',
    render: (associations) => (
      <PesticideGroupsCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'pesticide_groups'
        ? currentSorting.order
        : null,
    width: 150,
  },
  {
    title: t('active-substance|table.Resistance development risk'),
    dataIndex: 'pesticide_group_associations',
    key: 'resistance_risk',
    render: (associations) => (
      <ResistanceRiskCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'resistance_risk' ? currentSorting.order : null,
    width: 150,
  },
  {
    title: (
      <div className="py-[10px]">
        {t('active-substance|table.Risks on health')}
      </div>
    ),
    children: [
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Toxicity aigue')}
          </div>
        ),
        key: 'acute_toxicity',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.acute_toxicity_symbol}
            alt="Acute toxicity"
          />
        ),
        width: 10,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Long term effects')}
          </div>
        ),
        key: 'long_term_effects',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.long_term_effect_symbol}
            alt="Long term effects"
          />
        ),
        width: 10,
      },
    ],
  },
  {
    title: (
      <div className="py-[10px]">
        {t('active-substance|table.Risks on environment')}
      </div>
    ),
    children: [
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Fish or dophani')}
          </div>
        ),
        key: 'fish_daphnia',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.fishes_symbol}
            alt="Fish or daphnia"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Birds')}
          </div>
        ),
        key: 'birds',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.birds_symbol}
            alt="Birds"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Bees')}
          </div>
        ),
        key: 'bees',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.bees_symbol}
            alt="Bees"
          />
        ),
        width: 40,
      },

      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Persistance')}
          </div>
        ),
        key: 'persistence',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.persistence_symbol}
            alt="Persistence"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('active-substance|table.Leaching potential')}
          </div>
        ),
        key: 'leaching_potential',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.leaching_symbol}
            alt="Leaching potential"
          />
        ),
        width: 40,
      },
    ],
  },
  {
    title: (
      <div className="vertical-header-text">
        {t('active-substance|table.Active substance file')}
      </div>
    ),
    key: 'details',
    className: 'vertical-cell',
    render: (_, record) => (
      <ActionLinkCell
        href={`/matieres-actives/details/${record.id}`}
        target="_blank"
        className="btn btn--primary btn--sm hover:text-inherit"
      >
        {t('active-substance|table.See')}
      </ActionLinkCell>
    ),
    width: 40,
  },
];
